import { orgSelectors } from 'ducks/orgs'
import { List } from 'elements/react-admin/List'
import moment from 'moment'
import { ComponentVersions_3_0, ComponentVersionsInherit, makeStyles } from 'opensolar-ui'
import React, { useEffect } from 'react'
import {
  Datagrid,
  DateInput,
  FilterComp,
  FunctionField,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { getLocale } from 'storage/appStorage'
import { AccessRightsType } from 'types/global'
import { currencySymbolForCurrencyCode, formatCurrencyWithSymbol, taxNameForCountryIso2 } from 'util/misc'
import ReversalButton from './ReversalButton'
import WalletTransactionListToolbar from './WalletTransactionListToolbar'
import { useTransactionTaxInfo } from './useTransactionTaxInfo'
import { amountForWalletList, formatTaxRatePercent } from './walletTransactionAmountDisplay'

type PropTypes = {
  accessRights: AccessRightsType
  isGlobal: boolean
  resource?: string
  basePath?: string
  location?: unknown
  history?: unknown
  match: {
    isExact: boolean
    params: Readonly<Record<string, string | undefined>>
    path: string
    url: string
  }
  embedded?: boolean
}

const TRANSACTION_TYPE_CHOICES = [
  { id: 'purchase', name: 'Purchase' },
  { id: 'deposit', name: 'Deposit' },
  { id: 'gift', name: 'Gift' },
  { id: 'adjustment', name: 'Adjustment' },
  { id: 'refund', name: 'Refund' },
  { id: 'reversal', name: 'Reversal' },
  { id: 'other', name: 'Other' },
]

const Filters = (props: PropTypes) => {
  const isGlobalPage = props.resource === 'global_wallet_transactions'
  return (
    <FilterComp {...props}>
      <TextInput label="Product Purchased" source="product_purchased_name" />
      <TextInput label="Project Address" source="project_title" />
      <TextInput label="User Email" source="user_email" />
      <TextInput label="Invoice ID" source="invoice_id" />
      <SelectInput label="Transaction Type" source="transaction_type" choices={TRANSACTION_TYPE_CHOICES} />
      <DateInput label="Created After" source="created_date__gte" />
      <DateInput label="Created Before" source="created_date__lte" />
      {isGlobalPage && <TextInput label="Org Name" source="org_name" />}
    </FilterComp>
  )
}

const useStyles = makeStyles({
  actionCell: {
    minWidth: 120,
    width: 120,
    whiteSpace: 'nowrap',
    overflow: 'visible',
  },
  actionHeader: {
    minWidth: 120,
    width: 120,
  },
})

type WalletTxRecord = {
  currency?: string
  amount_inc_tax?: number
  transaction_type?: string
}

export const ValueIncTAX = ({ record }: { record?: WalletTxRecord }) => {
  if (!record) return null
  const rawAmount = record.amount_inc_tax
  if (rawAmount === undefined || rawAmount === null) return null
  const symbol = currencySymbolForCurrencyCode(record.currency)
  const displayAmount = amountForWalletList(rawAmount, record.transaction_type ?? 'other')
  return <>{formatCurrencyWithSymbol(displayAmount, symbol, getLocale())}</>
}

export const WalletTransactionList: React.FC<PropTypes> = (props) => {
  const { embedded, ...listProps } = props
  const ListComponent = List as React.ComponentType<Record<string, unknown>>
  useEffect(() => {
    moment.locale(window.locale)
  }, [])

  const isGlobalPage = props.resource === 'global_wallet_transactions'

  const countryIso2 = useSelector(orgSelectors.getOrgIso2)
  const taxName = taxNameForCountryIso2(countryIso2)
  const classes = useStyles()
  const subtitleNode = useTransactionTaxInfo(countryIso2 || '')

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <ListComponent
        {...listProps}
        hasCreate={isGlobalPage}
        hasSearch={false}
        filters={<Filters {...listProps} />}
        actions={<WalletTransactionListToolbar createButtonLabel="Create" hasExport={true} useCreateDialog={true} />}
        hideTitle={!!embedded}
        hideBreadCrumbs={!!embedded}
        subtitle={embedded ? undefined : subtitleNode}
      >
        <Datagrid>
          <FunctionField
            label="Invoice ID"
            source="id"
            render={(record) => (
              <p
                id="wallet_activity_id"
                style={{
                  fontWeight: 'bold',
                  fontSize: '1em',
                  margin: 0,
                  textOverflow: 'ellipsis',
                }}
              >
                <Link
                  to={(isGlobalPage ? '/global_wallet_transactions/' : '/wallet_transactions/') + record.id}
                  style={{}}
                >
                  {record.id}
                </Link>
              </p>
            )}
          />
          <FunctionField label="Created Date" render={(record) => moment(record.created_date).format('LL')} />
          <TextField source="transaction_line_items.0.product_purchased_name" label="Product Purchased" />
          {isGlobalPage && <TextField source="org_name" label="Org Name" />}
          <TextField source="user_email" label="User Email" />
          <TextField source="transaction_line_items.0.project_title" label="Project Address" />
          <TextField source="transaction_type_label" label="Transaction Type" />

          {isGlobalPage && <TextField source="transaction_line_items.0.units" label="Quantity" />}
          <FunctionField
            label="Amount (Inc. Tax)"
            source="amount_inc_tax"
            render={(record) => <ValueIncTAX record={record} />}
          />
          <FunctionField
            label={taxName}
            source="tax_combined"
            render={(record) => formatTaxRatePercent(record.tax_combined, record.amount_ex_tax, record.amount_inc_tax)}
          />
          {isGlobalPage && <TextField source="currency" label="Currency" />}
          {/* Ensure the cell is wide enough for the button and doesn't clip */}
          {isGlobalPage && (
            <FunctionField
              label="Reversal"
              cellClassName={classes.actionCell}
              headerClassName={classes.actionHeader}
              render={(record) => <ReversalButton record={record} />}
            />
          )}
        </Datagrid>
      </ListComponent>
    </ComponentVersionsInherit>
  )
}
export default WalletTransactionList
